<script setup>
// TODO: Change file name to LayoutMap (or create LayoutMap) add to layout folder
import { ref, computed, onMounted, watchEffect } from 'vue';
import { useSessionStore } from "@/stores/SessionStore";
import { useUserStore } from "@/stores/UserStore";
import { useMapUIStore } from "@/stores/MapUIStore";
import { useMapStore } from "@/stores/MapStore";
import { useStormStore } from "@/stores/StormStore";
import { useMapAdStore } from "@/stores/MapAdStore";
import { useMediaQuery } from '@/composables/useMediaQuery';
import { useStormLayers } from '@/composables/useStormLayers';
import MapSidebar from '@/components/MapSidebar.vue';
import MapName from '@/components/MapName.vue'
import MapRegionalNavigation from '@/components/MapRegionalNavigation.vue'
import MapControlsPrimary from '@/components/MapControlsPrimary.vue'
import MapControlsSecondary from '@/components/MapControlsSecondary.vue'
import MapUpgradeButton from '@/components/MapUpgradeButton.vue'
import MapLayerDashboards from '@/components/MapLayerDashboards.vue'
import MapTimeline from '@/components/MapTimeline.vue'
import MapModelSelector from '@/components/MapModelSelector.vue'
import MapUserProfileSelector from '@/components/MapUserProfileSelector.vue'
import MapLegend from '@/components/MapLegend.vue'
import SheetsContainer from '@/components/SheetsContainer.vue'
import MapMessages from "@/components/MapMessages.vue";
import MapStormHeader from "@/components/MapStormHeader.vue";
import StormMap from "@/components/StormMap.vue";
import MapDrawingTools from '@/components/MapDrawingTools.vue';
import MapLegacyInfoWindowInjects from '@/components/MapLegacyInfoWindowInjects.vue';
import MapToast from '@/components/MapToast.vue';
import MapFooterBannerAd from "@/components/MapFooterBannerAd.vue";
import useSettingsGlobals from '@/composables/useSettingsGlobals';
import useMapConfigurationGlobals from '@/composables/useMapConfigurationGlobals';
import useConfigurationGlobals from '@/composables/useConfigurationGlobals';
import useAdConfigurationGlobals from '@/composables/useAdConfigurationGlobals';

const { $BRAND } = useConfigurationGlobals();
const { $STORM_TRACK, $WEB_VIEW, $PLATFORM } = useSettingsGlobals();
const { $MAP_NAME_BLOCK, $STORM_HEADER_BLOCK } = useMapConfigurationGlobals();
const { $ADS_ALLOWED } = useAdConfigurationGlobals();   

window.$JWX = {}
window.$JWX.ready = false;

setPageDatasets();

const sessionStore = useSessionStore();
const stormStore = useStormStore();
await stormStore.getStormsData();

if (stormStore.hasActiveStorms) {
  await sessionStore.getToken();
  
  if (window.jweather) {
    window.jweather.token = sessionStore.token;
  } else {
    window.jweather = {
      token: sessionStore.token
    };
  }
}

const mapStore = useMapStore();
const mapUIStore = useMapUIStore();
const userStore = useUserStore();
const mapAdStore = useMapAdStore();

const isDesktop = useMediaQuery('(min-width: 840px)');
const isSmallDevice = useMediaQuery('(max-width: 839px)');

const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const stormId = urlParams.get('storm_id');
const region = urlParams.get('region');

if (stormId) {
  stormStore.setSelectedStorm(stormId);
  stormStore.visibleStorms.clear();
  stormStore.visibleStorms.add(stormId);
};

const mapReady = ref(false);
const jweatherReady = ref(false);

mapUIStore.updateTheme();
mapUIStore.toggleLegend();

const showModelSelector = computed(() => mapReady.value && mapStore.mapMode.toLowerCase() === 'fx' && mapStore.state.forecastSelectorNeeded);
const showProfileSelector = computed(() =>  mapReady.value && mapStore.state.searchType === 'profile');
const showUpgradeButton = computed(() => userStore.memberLevel < 49 && mapStore.state.obsOverlays['markers']);
const showMapMessages = computed(() => userStore.messages && userStore.messages.length > 0 && parseInt(userStore.nonTrialMemberLevel) < 49);
const showLayerDashboards = computed(() => mapReady.value && isDesktop.value && mapStore.state.obsOverlays.has('watches-warnings') || mapStore.state.obsOverlays.has('dsat-operating-condition') || mapStore.state.obsOverlays.has('lightning'));
const showDrawingTools = computed(() => mapReady.value && mapUIStore.showDrawingTools);
const showMapAd = computed(() => $ADS_ALLOWED && userStore.showAds && isDesktop);

onMounted(() => {
  if (stormStore.activeStorms.length === 0 && !$WEB_VIEW) {
    window.location = `https://wx.${$BRAND}.com/storm`;

    return;
  }
  mapUIStore.isStormTrackUI($STORM_TRACK);
  
  mapUIStore.$subscribe(() => {
    mapUIStore.updateTheme();
    mapUIStore.toggleLegend();
  });

  window.$EventBus.dispatchEvent('jwx-ready');

  jweatherReady.value = window.jweather?.ready || false;

  if (!jweatherReady.value) {
    window.$EventBus.addEventListener('jweather-ready', (e) => {
      jweatherReady.value = true;
    });
  }

});

function setPageDatasets() {
  const body = document.documentElement;

  if ($WEB_VIEW) {
    body.dataset.webView = true;
    body.dataset.platform = $PLATFORM.toLowerCase();
  }
}

watchEffect(() => {
  if (showMapAd.value) mapAdStore.refreshAd();
});

watchEffect(() => {
  if (mapReady.value) {
    useStormLayers(mapReady);
  }
});

</script>

<template>
  <StormMap v-model:mapReady="mapReady" :region="region" />
  <MapMessages v-if="showMapMessages" />
  <MapRegionalNavigation v-if="mapUIStore.showRegionalNavigation" />
  <MapSidebar v-if="mapReady" />
  <MapStormHeader v-if="$STORM_HEADER_BLOCK" />
  <div class="jwx-map__header" v-if="$MAP_NAME_BLOCK">
    <MapName />
  </div>
  <MapControlsPrimary :isSmallDevice="isSmallDevice" />
  <div v-if="isDesktop" class="jwx-map__shared">
    <MapControlsSecondary />
  </div>
  <MapUpgradeButton v-if="showUpgradeButton" />
  <MapLayerDashboards v-show="showLayerDashboards" />
  <Suspense>
    <MapModelSelector v-if="showModelSelector" />
  </Suspense>
  <Suspense>
    <MapUserProfileSelector v-if="showProfileSelector" />
  </Suspense>
  <Suspense>
    <MapTimeline v-if="mapReady" :isSmallDevice="isSmallDevice" />
  </Suspense>
  <MapDrawingTools v-if="showDrawingTools" :isSmallDevice="isSmallDevice"/>
  <MapFooterBannerAd />
  <MapLegend />
  <SheetsContainer />
  <MapLegacyInfoWindowInjects />
  <MapToast v-if="mapReady" />  
  <div v-if="isSmallDevice" class="jwx-map__bottom-container"></div>
</template>